import { useEffect, useState } from 'react';
import type { Asset } from '@/types/asset';
import type { DisposalDetails, DisposalAllocation } from '@/types/disposal';
import { dataService } from '@/services/data-service';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface DisposalAllocationStepProps {
  asset: Asset;
  details: DisposalDetails;
  allocation: DisposalAllocation;
  onAllocationChange: (allocation: DisposalAllocation) => void;
  onBack: () => void;
  onNext: () => void;
}

export function DisposalAllocationStep({
  asset,
  details,
  allocation,
  onAllocationChange,
  onBack,
  onNext,
}: DisposalAllocationStepProps): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const preview = await dataService.getDisposalPreview({
          assetId: asset.id,
          disposalDate: details.date,
          disposeOfMode: details.disposeOfMode,
          disposalQuantity: details.disposalQuantity,
          depreciationHandling: details.depreciationHandling,
        });

        // Set the allocation with preview values
        onAllocationChange({
          actualCost: preview.actualCost || 0,
          actualDepreciation: preview.actualDepreciation || 0,
          historicalCost: preview.historicalCost || 0,
          historicalDepreciation: preview.historicalDepreciation || 0,
          actualCostPercentage: preview.actualCostPercentage ?? 100,
          actualDepreciationPercentage: preview.actualDepreciationPercentage ?? 100,
          historicalCostPercentage: preview.historicalCostPercentage ?? 100,
          historicalDepreciationPercentage: preview.historicalDepreciationPercentage ?? 100,
          proceedsValue: preview.proceedsValue || 0,
        });
      } catch (err: any) {
        console.error('Failed to load disposal preview:', err);
        setError(err.message || 'Failed to load preview data');
        // Set default values on error
        onAllocationChange({
          actualCost: 0,
          actualDepreciation: 0,
          historicalCost: 0,
          historicalDepreciation: 0,
          actualCostPercentage: 100,
          actualDepreciationPercentage: 100,
          historicalCostPercentage: 100,
          historicalDepreciationPercentage: 100,
          proceedsValue: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [asset.id, details.date, details.disposeOfMode, details.disposalQuantity, details.depreciationHandling]);

  const handleValueChange = (field: keyof DisposalAllocation, value: string) => {
    const numValue = parseFloat(value) || 0;
    onAllocationChange({
      ...allocation,
      [field]: numValue,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
        <div className="flex justify-between pt-4 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const isPercentageMode = details.disposeOfMode === 'percentage';

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Allocation</h3>

        {isPercentageMode ? (
          // Percentage Mode Table
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium"></th>
                  <th className="text-left p-3 font-medium">Actual Cost %</th>
                  <th className="text-left p-3 font-medium">Historical Cost %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium">Asset Cost</td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.actualCostPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualCostPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.historicalCostPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalCostPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">Depreciation</td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.actualDepreciationPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualDepreciationPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.historicalDepreciationPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalDepreciationPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">Proceeds Value</td>
                  <td className="p-3" colSpan={2}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.proceedsValue || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('proceedsValue', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          // Value/Quantity Mode Table
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium"></th>
                  <th className="text-left p-3 font-medium">Actual Cost</th>
                  <th className="text-left p-3 font-medium">Historical Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium">Asset Cost</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.actualCost || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualCost', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.historicalCost || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalCost', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">Depreciation</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.actualDepreciation || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualDepreciation', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.historicalDepreciation || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalDepreciation', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">Proceeds Value</td>
                  <td className="p-3" colSpan={2}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.proceedsValue || 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('proceedsValue', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4 border-t">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
        >
          Next
        </button>
      </div>
    </div>
  );
}
